import React from "react"
import { Link } from "gatsby"
import Box from "@mui/material/Box"
import Container from "@mui/material/Container"
import Typography from "@mui/material/Typography"
import MuiLink from "@mui/material/Link"
import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"

import { useT } from "i18n/translations"
import { palette } from "theme/colors"

const Footer = ({
  lang,
  navItems = [],
  siteSettings = {},
  allLanguages = [],
}) => {
  const tr = useT(lang)
  const year = new Date().getFullYear()

  const bookingUrl =
    siteSettings.bookingUrl ||
    process.env.GATSBY_BOOKING_URL ||
    "https://www.booking.com/hotel/pt/casa-da-avo-miquinhas.pt-pt.html"
  const address =
    siteSettings.address ||
    "Rua de Barrimau, 4\n4590-750 Paços de Ferreira, Portugal"
  const rating = siteSettings.bookingRating || "9.5"
  const footerLinks = navItems.filter(
    (i) => i.type === "normal" || i.type === "soon" || !i.type,
  )

  const linkSx = {
    color: "rgba(255,255,255,0.55)",
    textDecoration: "none",
    fontSize: "15px",
    fontWeight: 400,
    display: "block",
    "&:hover": { color: palette.white },
    transition: "color 0.15s",
  }

  return (
    <Box
      component="footer"
      sx={{ bgcolor: palette.primary, color: palette.white }}
    >
      <Container
        maxWidth="xl"
        sx={{
          px: { xs: 4, md: 8 },
          pt: { xs: 6, md: 10 },
          pb: { xs: 4, md: 6 },
        }}
      >
        {/* Top grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "1.5fr 1fr 1fr 1.5fr",
            },
            gap: { xs: 6, lg: 8 },
            mb: 8,
          }}
        >
          {/* Brand column */}
          <Box>
            <Box sx={{ mb: 3 }}>
              <img
                src="/images/logo.png"
                alt={siteSettings.siteTitle || "Casa da Avó Miquinhas"}
                style={{ height: 36, filter: "brightness(0) invert(1)" }}
              />
            </Box>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "14px",
                lineHeight: 1.7,
                whiteSpace: "pre-line",
                mb: 2,
              }}
            >
              {address}
            </Typography>
          </Box>

          {/* Navigation */}
          <Box>
            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                mb: 3,
              }}
            >
              {tr.footer.navigation}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {footerLinks.map((item, i) =>
                item.type === "soon" ? (
                  <Box
                    key={i}
                    aria-disabled="true"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      opacity: 0.45,
                      cursor: "default",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        ...linkSx,
                        cursor: "default",
                        pointerEvents: "none",
                        "&:hover": { color: "rgba(255,255,255,0.55)" },
                      }}
                    >
                      {item.link.title}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "10px",
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        px: 1,
                        py: 0.25,
                        borderRadius: "100px",
                        bgcolor: "rgba(255,255,255,0.1)",
                        color: palette.accent,
                        flexShrink: 0,
                      }}
                    >
                      {tr.nav.soon}
                    </Box>
                  </Box>
                ) : (
                  <MuiLink
                    key={i}
                    component={Link}
                    to={item.link.url}
                    sx={linkSx}
                    underline="none"
                  >
                    {item.link.title}
                  </MuiLink>
                ),
              )}
            </Box>
          </Box>

          {/* Contact */}
          <Box>
            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                mb: 3,
              }}
            >
              {tr.footer.contact}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <MuiLink
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  ...linkSx,
                  fontWeight: 500,
                }}
                underline="none"
              >
                {tr.footer.bookOnBooking}
              </MuiLink>
              <MuiLink
                component={Link}
                to="/politica-de-privacidade"
                sx={linkSx}
                underline="none"
              >
                {tr.footer.privacy}
              </MuiLink>
            </Box>
          </Box>

          {/* CTA column */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Button
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              sx={{
                color: palette.white,
                bgcolor: palette.accent,
                borderRadius: "100px",
                py: 1.75,
                fontSize: "15px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: palette.accentDark },
              }}
            >
              {tr.header.bookNow} →
            </Button>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                alignSelf: { xs: "center", lg: "flex-start" },
              }}
            >
              <Box
                component="img"
                src="/images/footer-funding-logos2.png"
                alt="PRR — Plano de Recuperação e Resiliência, República Portuguesa, Financiado pela União Europeia NextGenerationEU"
                sx={{
                  width: "100%",
                  maxWidth: 420,
                  height: "auto",
                  display: "block",
                }}
              />
              <MuiLink
                href="/documents/ficha-do-projeto-20134.pdf"
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  ...linkSx,
                  fontSize: "13px",
                  alignSelf: { xs: "center", lg: "flex-start" },
                }}
              >
                Ficha do Projeto 20134(pdf)
              </MuiLink>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        {/* Bottom bar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            pt: 3,
          }}
        >
          <Typography sx={{ fontSize: "13px" }}>
            © {year} {tr.footer.copyright}
          </Typography>

          {/* Language switcher */}
          {allLanguages.length > 1 && (
            <Box sx={{ display: "flex", gap: 2 }}>
              {allLanguages.map((l) => (
                <MuiLink
                  key={l.code}
                  component={Link}
                  to={l.urlPrefix || "/"}
                  underline="none"
                  sx={{
                    color:
                      l.code === lang?.toUpperCase()
                        ? palette.white
                        : "rgba(255,255,255,0.3)",
                    fontSize: "13px",
                    fontWeight: l.code === lang?.toUpperCase() ? 600 : 400,
                    "&:hover": { color: palette.white },
                    transition: "color 0.15s",
                  }}
                >
                  {l.code}
                </MuiLink>
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  )
}

export default Footer
